/** Project settings: profile, time zone, hardware, CUE flag, images, delete project. */
import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { api, ApiError } from "../api/client";
import { keys, useMe, useProject } from "../api/hooks";
import type { Project } from "../api/types";
import { can } from "../components/AppShell";
import { StageIntro } from "../components/sat";
import { ConfirmDialog, ErrorNotice, Field, FileDrop, Notice, Spinner } from "../components/ui";
import { uploadFile } from "../lib/files";

const BOUNDARIES = [
  ["ess_container", "ESS container output (battery/DC)"],
  ["poc", "Point of connection (POC)"],
] as const;

function timeZones(): string[] {
  const supported = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
  return supported ? supported("timeZone") : ["Europe/Copenhagen", "UTC"];
}

interface Form {
  label: string;
  client_name: string;
  capacity_label: string;
  timezone: string;
  commissioning_date: string;
  boundary_code: string;
  default_cpd: string;
  pcs_count: string;
  pcs_rating_kw: string;
  smartlogger_count: string;
  include_cue: boolean;
}

function toForm(project: Project): Form {
  const hardware = project.hardware as Record<string, number | undefined>;
  return {
    label: project.label,
    client_name: project.client_name ?? "",
    capacity_label: project.capacity_label ?? "",
    timezone: project.timezone,
    commissioning_date: project.commissioning_date ?? "",
    boundary_code: String((project.boundary as { code?: string }).code ?? "ess_container"),
    default_cpd: project.default_cpd,
    pcs_count: hardware.pcs_count?.toString() ?? "",
    pcs_rating_kw: hardware.pcs_rating_kw?.toString() ?? "",
    smartlogger_count: hardware.smartlogger_count?.toString() ?? "",
    include_cue: !!(project.flags as { include_cue_in_overall_status?: boolean }).include_cue_in_overall_status,
  };
}

function ImageField(props: { pid: string; project: Project; field: "card_photo" | "client_logo"; title: string; hint: string; onSaved: (project: Project) => void }) {
  const { pid, project, field } = props;
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const current = project[field];

  useEffect(() => {
    let cancelled = false;
    setPreview(null);
    if (current) {
      api
        .get<{ url: string }>(`/projects/${pid}/assets/${field}?format=json`)
        .then((result) => !cancelled && setPreview(result.url))
        .catch(() => undefined);
    }
    return () => {
      cancelled = true;
    };
  }, [pid, field, current]);

  async function save(value: string | null) {
    const updated = await api.patch<Project>(`/projects/${pid}`, { version: project.version, [field]: value });
    props.onSaved(updated);
  }

  async function upload(file: File) {
    setError(null);
    try {
      const uploaded = await uploadFile(`/projects/${pid}/uploads`, file, "asset", {}, setProgress);
      await save(uploaded.key);
    } catch (reason) {
      setError(reason);
    } finally {
      setProgress(null);
    }
  }

  return (
    <div className="stack" style={{ gap: "0.5rem" }}>
      <strong>{props.title}</strong>
      {preview && <img src={preview} alt={props.title} style={{ maxHeight: 120, maxWidth: "100%", objectFit: "contain", borderRadius: 8, border: "1px solid var(--border)" }} />}
      <FileDrop
        accept="image/png,image/jpeg,image/webp"
        disabled={!!progress}
        onFiles={(files) => files[0] && upload(files[0])}
        label={progress ? <Spinner label={progress.replace(/…$/, "")} /> : <span className="small">{current ? "Replace the image" : "Drop a PNG, JPG or WEBP"} — {props.hint}</span>}
      />
      {current && (
        <button type="button" className="btn small ghost" onClick={() => save(null).catch(setError)}>
          Remove image
        </button>
      )}
      <ErrorNotice error={error} />
    </div>
  );
}

export function ProjectSettingsPage() {
  const { pid = "" } = useParams();
  const [params] = useSearchParams();
  const me = useMe();
  const project = useProject(pid);
  const client = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState<Form | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [savedAt, setSavedAt] = useState(0);
  const zones = useMemo(timeZones, []);

  const version = project.data?.version;
  useEffect(() => {
    if (project.data) setForm(toForm(project.data));
    // Reset the form whenever a new version of the project arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, pid]);

  if (project.isLoading || !form) return project.error ? <ErrorNotice error={project.error} /> : <Spinner label="Loading the settings" />;
  if (me.data && !can(me.data, "project.edit")) return <Notice kind="info">Project settings are managed by the project team.</Notice>;
  const data = project.data!;
  const set = <K extends keyof Form>(field: K, value: Form[K]) => setForm({ ...form, [field]: value });

  const accept = (updated: Project) => {
    client.setQueryData(keys.project(pid), updated);
    void client.invalidateQueries({ queryKey: keys.projects });
    void client.invalidateQueries({ queryKey: keys.programme(pid) });
  };

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!form) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await api.patch<Project>(`/projects/${pid}`, {
        version: data.version,
        label: form.label,
        client_name: form.client_name,
        capacity_label: form.capacity_label,
        timezone: form.timezone,
        commissioning_date: form.commissioning_date || null,
        boundary_code: form.boundary_code,
        default_cpd: form.default_cpd,
        hardware: {
          pcs_count: form.pcs_count === "" ? null : Number(form.pcs_count),
          pcs_rating_kw: form.pcs_rating_kw === "" ? null : Number(form.pcs_rating_kw),
          smartlogger_count: form.smartlogger_count === "" ? null : Number(form.smartlogger_count),
        },
        flags: { include_cue_in_overall_status: form.include_cue },
      });
      accept(updated);
      setSavedAt(Date.now());
    } catch (reason) {
      setError(reason);
    } finally {
      setBusy(false);
    }
  }

  const conflict = error instanceof ApiError && error.code === "version_conflict";
  const cpds = Object.keys(data.guarantee_schedules);
  return (
    <div className="stack" style={{ gap: "1.4rem", maxWidth: 920 }}>
      <StageIntro title="Project settings" description="Read from the performance guarantee when the project was created. Everything stays editable." status={data.label} />
      {params.get("created") && (
        <Notice kind="success" title="Project created">
          Check the values read from the guarantee, then <Link to={`/p/${pid}`}>open the programme</Link>.
        </Notice>
      )}
      <form className="stack" onSubmit={submit}>
        <section className="card stack">
          <h3>Profile</h3>
          <div className="grid-2">
            <Field label="Project name">
              <input className="input" required maxLength={120} value={form.label} onChange={(event) => set("label", event.target.value)} />
            </Field>
            <Field label="Client">
              <input className="input" maxLength={120} value={form.client_name} onChange={(event) => set("client_name", event.target.value)} />
            </Field>
            <Field label="Capacity label" hint="Shown on the portfolio card, e.g. “30 MW / 120 MWh”.">
              <input className="input" maxLength={60} value={form.capacity_label} onChange={(event) => set("capacity_label", event.target.value)} />
            </Field>
            <Field label="Time zone" hint="Used for every date and time in the app and the reports. Evidence must be processed again after a change.">
              <input className="input" list="time-zones" required value={form.timezone} onChange={(event) => set("timezone", event.target.value)} />
              <datalist id="time-zones">
                {zones.map((zone) => (
                  <option key={zone} value={zone} />
                ))}
              </datalist>
            </Field>
          </div>
        </section>

        <section className="card stack">
          <h3>Programme and contract</h3>
          <div className="grid-2">
            <Field
              label="Commissioning test date"
              hint={
                data.commissioning_date_source === "derived"
                  ? `Currently derived from the saved Year 0 result (${data.effective_commissioning_date}). Set a date to fix it.`
                  : "Anchors the annual SAT dates for Years 1–15. Left empty, it follows the saved Year 0 result."
              }
            >
              <input className="input" type="date" value={form.commissioning_date} onChange={(event) => set("commissioning_date", event.target.value)} />
            </Field>
            <Field label="Measurement boundary" hint="The analysis supports the ESS container boundary; evidence for a POC project is refused.">
              <select className="input" value={form.boundary_code} onChange={(event) => set("boundary_code", event.target.value)}>
                {BOUNDARIES.map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Cycles per day (CPD)" hint="Selects the guarantee schedule.">
              <select className="input" value={form.default_cpd} onChange={(event) => set("default_cpd", event.target.value)}>
                {cpds.map((cpd) => (
                  <option key={cpd} value={cpd}>
                    {cpd}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="ESS nameplate energy">
              <input className="input" disabled value={data.ess_nameplate_energy_mwh ? `${data.ess_nameplate_energy_mwh} MWh (from the guarantee)` : "Not found in the guarantee"} />
            </Field>
          </div>
          <label className="checkbox">
            <input type="checkbox" checked={form.include_cue} onChange={(event) => set("include_cue", event.target.checked)} />
            <span>
              Include the committed CUE in the overall PASS rule
              <br />
              <span className="muted small">Off: the overall status is decided by RTE and CUEP. The criteria are printed in the PDF and the audit workbook.</span>
            </span>
          </label>
        </section>

        <section className="card stack">
          <h3>Hardware for the PPC setpoint plan</h3>
          <div className="grid-3">
            <Field label="PCS count">
              <input className="input" type="number" min={1} step={1} value={form.pcs_count} onChange={(event) => set("pcs_count", event.target.value)} />
            </Field>
            <Field label="PCS rating (kW)">
              <input className="input" type="number" min={0} step="any" value={form.pcs_rating_kw} onChange={(event) => set("pcs_rating_kw", event.target.value)} />
            </Field>
            <Field label="SmartLogger count">
              <input className="input" type="number" min={1} step={1} value={form.smartlogger_count} onChange={(event) => set("smartlogger_count", event.target.value)} />
            </Field>
          </div>
        </section>

        <ErrorNotice error={error} title={conflict ? "Someone else changed this project in the meantime" : "The settings could not be saved"} />
        <div className="row end">
          {savedAt > 0 && !error && <span className="muted small" role="status">Saved.</span>}
          {conflict && (
            <button type="button" className="btn" onClick={() => { setError(null); void client.invalidateQueries({ queryKey: keys.project(pid) }); }}>
              Reload
            </button>
          )}
          <button className="btn primary" disabled={busy}>
            {busy ? "Saving…" : "Save settings"}
          </button>
        </div>
      </form>

      <section className="card stack">
        <h3>Images</h3>
        <div className="grid-2">
          <ImageField pid={pid} project={data} field="card_photo" title="Portfolio card photo" hint="site photograph" onSaved={accept} />
          <ImageField pid={pid} project={data} field="client_logo" title="Client logo" hint="printed on the client report" onSaved={accept} />
        </div>
      </section>

      {can(me.data, "project.delete") && (
        <section className="card stack">
          <h3>Delete project</h3>
          <p className="muted small" style={{ margin: 0 }}>
            The project disappears for everyone. Its evidence, results and reports are kept, and a platform administrator can restore it.
          </p>
          <div>
            <ConfirmDialog
              trigger={<button className="btn danger">Delete project…</button>}
              title={`Delete ${data.label}?`}
              description="The project is hidden from the portfolio. Nothing is erased; ask a platform administrator to restore it."
              confirmLabel="Delete project"
              danger
              onConfirm={async () => {
                await api.delete(`/projects/${pid}?version=${data.version}`);
                await client.invalidateQueries({ queryKey: keys.projects });
                navigate("/", { replace: true });
              }}
            />
          </div>
        </section>
      )}
    </div>
  );
}
